import { useMemo, useRef, useState, useEffect } from 'react';

import { Box, Button, Text, useClipboard, VStack } from '@chakra-ui/react';

import { Skeleton } from 'components/uikit';
import { DynamicCopyIcon } from 'components/uikit/CopyWrapper';
import { useIsMobile } from 'utils/hooks/screenBreakpoints';

import { ImageCallout } from './paymentInstructionUtils';

interface ImageSlideWithCalloutsProps {
  src: string;
  alt: string;
  callouts: ImageCallout[];
}

interface CopyableDataRowProps {
  value: string;
}

function CopyableDataRow({
  value,
  isMobile,
}: CopyableDataRowProps & { isMobile?: boolean }) {
  const { onCopy, hasCopied } = useClipboard(value);

  return (
    <Button
      bg='rgba(255, 255, 255, 0.1)'
      color='white'
      cursor='pointer'
      borderRadius={isMobile ? '5px' : '6px'}
      px={isMobile ? '6px' : '8px'}
      py={isMobile ? '5px' : '6px'}
      h='auto'
      minH='auto'
      _hover={{ bg: 'rgba(255, 255, 255, 0.15)' }}
      onClick={(e) => {
        e.stopPropagation();
        onCopy();
      }}
      aria-label='Copy data'
      type='button'
      gap={isMobile ? '4px' : '6px'}
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      w='100%'
      fontSize={isMobile ? 'xs' : 'sm'}
      fontWeight='medium'
    >
      <Text
        fontSize={isMobile ? 'sm' : 'md'}
        color='white'
        fontWeight='medium'
        flex='1'
        textAlign='left'
      >
        {value}
      </Text>
      <DynamicCopyIcon
        hasCopied={hasCopied}
        boxSize={isMobile ? '12px' : '14px'}
        color={hasCopied ? '#4ade80' : 'white'}
      />
    </Button>
  );
}

export function ImageSlideWithCallouts({
  src,
  alt,
  callouts,
}: ImageSlideWithCalloutsProps) {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageBox, setImageBox] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  // Reset loading state when src changes
  useEffect(() => {
    setIsImageLoaded(false);
    setImageBox(null);
  }, [src]);

  useEffect(() => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;

    // Function to calculate and set image box
    // When using object-fit: contain, we need to calculate the actual rendered image dimensions
    const calculateImageBox = () => {
      const containerRect = container.getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();
      const imgElement = img;

      if (
        !imgElement.naturalWidth ||
        !imgElement.naturalHeight ||
        imgRect.width === 0 ||
        imgRect.height === 0
      )
        return;

      // Get the actual rendered image element dimensions
      const imgElementWidth = imgRect.width;
      const imgElementHeight = imgRect.height;

      const naturalWidth = imgElement.naturalWidth;
      const naturalHeight = imgElement.naturalHeight;
      const naturalAspectRatio = naturalWidth / naturalHeight;
      const elementAspectRatio = imgElementWidth / imgElementHeight;

      let renderedWidth: number;
      let renderedHeight: number;

      if (naturalAspectRatio > elementAspectRatio) {
        // Image is wider - fit to element width
        renderedWidth = imgElementWidth;
        renderedHeight = imgElementWidth / naturalAspectRatio;
      } else {
        // Image is taller - fit to element height
        renderedHeight = imgElementHeight;
        renderedWidth = imgElementHeight * naturalAspectRatio;
      }

      const offsetX = Math.round((imgElementWidth - renderedWidth) / 2);
      const offsetY = Math.round((imgElementHeight - renderedHeight) / 2);

      const leftRelativeToContainer = Math.round(
        imgRect.left - containerRect.left + offsetX,
      );
      const topRelativeToContainer = Math.round(
        imgRect.top - containerRect.top + offsetY,
      );
      const roundedWidth = Math.round(renderedWidth);
      const roundedHeight = Math.round(renderedHeight);

      if (roundedWidth > 0 && roundedHeight > 0) {
        setImageBox({
          left: leftRelativeToContainer,
          top: topRelativeToContainer,
          width: roundedWidth,
          height: roundedHeight,
        });
        setIsImageLoaded(true);
      }
    };

    // Wait for image to load to get natural dimensions
    const handleImageLoad = () => {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        calculateImageBox();
      });
    };

    let timeoutId: NodeJS.Timeout | null = null;

    if (img.complete && img.naturalWidth > 0) {
      // Image already loaded
      handleImageLoad();
    } else {
      img.addEventListener('load', handleImageLoad);
      // Also try after a short delay in case load event doesn't fire
      timeoutId = setTimeout(() => {
        if (img.complete && img.naturalWidth > 0) {
          calculateImageBox();
        }
      }, 100);
    }

    const compute = () => {
      if (img.complete && img.naturalWidth > 0) {
        // Use double requestAnimationFrame for better layout stability on mobile
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            calculateImageBox();
          });
        });
      }
    };

    const ro = new ResizeObserver(compute);
    ro.observe(container);
    ro.observe(img);
    window.addEventListener('resize', compute);

    let visualViewport: VisualViewport | null = null;
    if (typeof window !== 'undefined' && window.visualViewport) {
      visualViewport = window.visualViewport;
      visualViewport.addEventListener('resize', compute);
      visualViewport.addEventListener('scroll', compute);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      img.removeEventListener('load', handleImageLoad);
      ro.disconnect();
      window.removeEventListener('resize', compute);
      if (visualViewport) {
        visualViewport.removeEventListener('resize', compute);
        visualViewport.removeEventListener('scroll', compute);
      }
    };
  }, [src]);

  const placedCallouts = useMemo(() => {
    if (!imageBox) return [];

    return callouts.map((c) => {
      const xPx = imageBox.left + imageBox.width * c.x;
      const yPx = imageBox.top + imageBox.height * c.y;

      const markerPos = c.markerPosition ?? 'auto';

      const xThreshold = isMobile ? 0.55 : 0.62;
      const yThreshold = isMobile ? 0.55 : 0.62;
      const placeLeft = c.x > xThreshold;
      let placeAbove = c.y > yThreshold;

      if (markerPos === 'top') {
        placeAbove = false;
      } else if (markerPos === 'bottom') {
        placeAbove = true;
      }
      // If 'auto', use the heuristic values calculated above

      const boxTransform = `translate(${placeLeft ? '-100%' : '0'}, ${
        placeAbove ? '-100%' : '0'
      })`;

      return {
        ...c,
        xPx,
        yPx,
        placeLeft,
        placeAbove,
        boxTransform,
        markerPosition: markerPos,
      };
    });
  }, [callouts, imageBox, isMobile]);

  return (
    <Box ref={containerRef} position='relative' h='100%'>
      <Box position='relative' w='100%' h='100%'>
        {!isImageLoaded && (
          <Skeleton
            position='absolute'
            top='0'
            left='0'
            w='100%'
            h='100%'
            borderRadius='8px'
            zIndex={1}
          />
        )}
        <Box
          as='img'
          ref={imgRef}
          src={src}
          alt={alt}
          w='100%'
          h='100%'
          sx={{ objectFit: 'contain' }}
          opacity={isImageLoaded ? 1 : 0}
          transition='opacity 0.2s'
        />
      </Box>

      {isImageLoaded && imageBox
        ? placedCallouts.map((c) => (
            <Box
              key={c.id}
              position='absolute'
              left={`${c.xPx}px`}
              top={`${c.yPx}px`}
              zIndex={2}
              transform={c.boxTransform}
              pointerEvents='none'
            >
              {/* Description box with arrow pointer */}
              <Box
                position='relative'
                maxW={isMobile ? ['280px', '320px'] : ['320px', '600px']}
              >
                {/* Triangular pointer */}
                <Box
                  position='absolute'
                  w='0'
                  h='0'
                  left='50%'
                  transform='translateX(-50%)'
                  {...(c.placeAbove
                    ? {
                        bottom: isMobile ? '-10px' : '-12px',
                        borderLeft: isMobile
                          ? '10px solid transparent'
                          : '12px solid transparent',
                        borderRight: isMobile
                          ? '10px solid transparent'
                          : '12px solid transparent',
                        borderTop: isMobile
                          ? '10px solid #2D2D2D'
                          : '12px solid #2D2D2D',
                      }
                    : {
                        top: isMobile ? '-10px' : '-12px',
                        borderLeft: isMobile
                          ? '10px solid transparent'
                          : '12px solid transparent',
                        borderRight: isMobile
                          ? '10px solid transparent'
                          : '12px solid transparent',
                        borderBottom: isMobile
                          ? '10px solid #2D2D2D'
                          : '12px solid #2D2D2D',
                      })}
                  zIndex={1}
                />
                {/* Description box content */}
                <Box
                  bg='#2D2D2D'
                  color='white'
                  borderRadius={isMobile ? '10px' : '12px'}
                  px={isMobile ? '10px' : '12px'}
                  py={isMobile ? '8px' : '10px'}
                  position='relative'
                  zIndex={2}
                  pointerEvents='auto'
                  width={isMobile ? ['180px', '200px'] : ['200px', '230px']}
                >
                  {c.title ? (
                    <Text
                      fontSize={isMobile ? 'sm' : 'md'}
                      fontWeight='semibold'
                      mb={isMobile ? '3px' : '4px'}
                      color='white'
                    >
                      {c.title}
                    </Text>
                  ) : null}
                  <Text
                    fontSize={isMobile ? 'sm' : 'md'}
                    color='white'
                    mb={c.copyableData ? (isMobile ? '6px' : '8px') : '0'}
                    lineHeight={isMobile ? '1.4' : '1.5'}
                  >
                    {c.description}
                  </Text>
                  {c.copyableData ? (
                    Array.isArray(c.copyableData) ? (
                      <VStack
                        spacing={isMobile ? '4px' : '6px'}
                        w='100%'
                        align='stretch'
                      >
                        {c.copyableData.map((value, index) => (
                          <CopyableDataRow
                            key={index}
                            value={value}
                            isMobile={isMobile}
                          />
                        ))}
                      </VStack>
                    ) : (
                      <CopyableDataRow
                        value={c.copyableData}
                        isMobile={isMobile}
                      />
                    )
                  ) : null}
                </Box>
              </Box>
            </Box>
          ))
        : null}
    </Box>
  );
}
